/**
 * Route helpers for ClientsCache — only /dashboard and /clients (list) consume the cache.
 * Import useClientsCache only from dashboard + clients list components.
 */

import { SessionCredentialError } from '@/lib/session-recovery';

export const CLIENT_CACHE_LAYOUT_PATHS = ['/dashboard', '/clients'] as const;

/** Provider layout routes (mount ClientsCacheProvider) — exact paths only. */
export function isClientsCacheLayoutRoute(pathname: string): boolean {
  return pathname === '/dashboard' || pathname === '/clients';
}

/** Routes that run the legacy assessment/posture batch fetch (dashboard only until detail hook lands). */
export function isClientsCacheBatchRoute(pathname: string): boolean {
  return pathname === '/dashboard';
}

/** Stable key for client-list change detection (sorted IDs). */
export function computeClientIdsKey(clients: ReadonlyArray<{ id: string }>): string {
  return clients
    .map((c) => c.id)
    .sort()
    .join(',');
}

/** IDs present in key but not yet fetched for batch cache. */
export function getClientIdsMissingFromCache(
  clientIdsKey: string,
  cachedIds: ReadonlySet<string>
): string[] {
  if (!clientIdsKey) return [];
  return clientIdsKey.split(',').filter((id) => id.length > 0 && !cachedIds.has(id));
}

/** True when poll/clients API error looks like a permission denial. */
export function isClientsCachePermissionError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return message.includes('403') || /forbidden/i.test(message);
}

/** True when Session recovery is sending the user to sign in. Not a roster data failure. */
export function isClientsCacheAuthError(error: unknown): boolean {
  return error instanceof SessionCredentialError && error.outcome === 'signed-out';
}
